// Diego, junta del 13-oct (segunda tanda): «los avisos confunden mucho: son tantos y tanto texto que los ignoramos; mejor
// que sean cosas importantes y que no se repitan». La pantalla enseña a la vista SOLO lo importante (máximo 3, sin repetidos,
// una línea corta cada uno); todo lo demás queda en «Ver todos (N)». Aquí vive la regla para que no dependa de la pantalla.
import type { Aviso, CodigoAviso } from './avisos.ts';
import type { OpcionId } from './basicos.ts';
import type { Cuestionario } from './cuestionario.ts';
import { numero } from '../dinero.ts';

/** Códigos IMPORTANTES, en orden de importancia (el primero, el más importante): precio en rojo o abajo del piso, dato del
 *  recibo que falta o no cuadra, regulación y estructura cobrada dos veces. Los demás (info, amarillos menores, técnicos y
 *  los de la revisión con IA) van al plegable «Ver todos». */
export const CODIGOS_AVISO_IMPORTANTES: readonly CodigoAviso[] = Object.freeze([
  // Precio
  'PIERDE_DINERO',
  'DEBAJO_DEL_PISO',
  'MARGEN_BAJO',
  'DESCUENTO_SIN_APROBAR',
  'PRECIO_OPTIMUS',
  'COSTO_SIN_MONTO',
  // Diego, 24-oct: fuera de Hermosillo sin viáticos (el PDF no sale)
  'FALTAN_VIATICOS',
  // z46-7 (P19): multisitio, viáticos de la ciudad en otra sucursal (o cobrados dos veces: el PDF de todas no sale)
  'VIATICOS_DE_LA_CIUDAD',
  // Dato del recibo que falta o no cuadra
  'FALTA_DATO',
  'RECIBO_ILEGIBLE',
  'RECIBO_INCOMPLETO',
  'RECIBO_NO_ES_CFE',
  'RECIBOS_DE_OTRO_SERVICIO',
  'TARIFA_NO_COINCIDE',
  'FACTURACION_NO_COINCIDE',
  'RECIBO_QUITADO',
  'RECIBO_FUERA_DEL_ANIO',
  'RECIBO_REPETIDO',
  // Verificación del 13-oct: lo que cobró CFE no cuadra, o ya tiene paneles (la cotización saldría corta)
  'DATO_DUDOSO',
  // Diego, 25-oct: con 11 recibos se sigue y lo que falta se estima. «Faltan recibos» va DETRÁS de lo que cambia las cifras
  // (un recibo que no se usó, un renglón dudoso): con un rojo, los dos quedaban en «Ver todos» (verificador de la tanda 1b).
  'FALTAN_RECIBOS',
  'YA_TIENE_PANELES',
  // Ley y doble cobro
  'REGULACION',
  'ESTRUCTURA_DOBLE',
] as const);

/** Diego, 13-oct (22:30, ADENDA «LÍMITE LEGAL: SI NO SE PUEDE, NO SE PUEDE»): la generación distribuida es de centrales
 *  «con capacidad menor a 0.7 MW» (LSE art. 25). Arriba de eso Solarix NO cotiza y nunca ofrece el permiso de la CNE como
 *  salida. El tope duro va en los kW de los inversores (CA, la lectura de Diego); los kWp de paneles que llegan a 0.7 MW son
 *  un amarillo de riesgo. El motor lo trae en datos/regulacion-gd.json (umbrales.gd_kw) y truena al cargar si no es este. */
export const LIMITE_LEY_GD_KW = 700;

/** La frase de los avisos del límite de la ley (los del motor la llevan siempre: así se reconocen sin un código nuevo). */
export const FRASE_LIMITE_LEY = 'arriba de 0.7 MW no se puede';
/** …también el texto de antes del 13-oct (22:30) del rojo GD-07-ROJO, que los borradores viejos guardan hasta que se
 *  recalculan: sin esto la pantalla no apagaba el botón del PDF (verificación adversarial del 14-oct). */
const RE_LIMITE_LEY = /arriba de 0\.7 MW no se puede|700 kW o más en inversores ya no es generación distribuida/i;

/** ¿Es un aviso del límite de la ley (la opción quedó en el máximo, o pasa el límite y no se cotiza)? Va PRIMERO a la vista. */
export function esAvisoLimiteLey(a: Pick<Aviso, 'codigo' | 'mensaje'>): boolean {
  return a.codigo === 'REGULACION' && RE_LIMITE_LEY.test(a.mensaje);
}

/** La frase del amarillo de la ley que pide el OK de Diego (GD-07-AMBAS-LECTURAS: los inversores quedan abajo de 700 kW
 *  pero los paneles suman 700 kWp o más, y la regla de cómo se mide aún no se publica). La reverificación del 13-oct dice
 *  «Amarillo: con OK de Diego». El motor truena al cargar si el texto de la regla ya no la trae. */
export const FRASE_LEY_OK_DE_DIEGO = 'no lo ofrezcas sin el OK de Diego';
const RE_LEY_OK_DE_DIEGO = /no lo ofrezcas sin el OK de Diego/i;

/** ¿Es un aviso de la ley que Diego tiene que ver? El límite (tope, opción quitada, rojo) o un amarillo de la ley que pide
 *  su OK. Va a la vista junto al límite y, al sacar el PDF, a la bitácora de Diego (verificación adversarial del 14-oct: el
 *  de los 700 kWp quedaba en «Ver todos» y a Diego no le llegaba nada). */
export function esAvisoDeLaLeyParaDiego(a: Pick<Aviso, 'codigo' | 'mensaje'>): boolean {
  return esAvisoLimiteLey(a) || (a.codigo === 'REGULACION' && RE_LEY_OK_DE_DIEGO.test(a.mensaje));
}

/** z20 (Diego, 15-oct): un GDMTH de San Luis Río Colorado o Puerto Peñasco (División Baja California) se calcula con las cuotas
 *  de BC, pero su horario de punta no está modelado. Ámbar para el vendedor que también le llega a Diego; NO detiene el PDF. */
export const FRASE_GDMTH_BAJA_CALIFORNIA = 'revísalo con Diego, el horario de punta de Baja California no está modelado';
export function esAvisoGdmthBajaCalifornia(a: Pick<Aviso, 'mensaje'>): boolean {
  return a.mensaje.includes(FRASE_GDMTH_BAJA_CALIFORNIA);
}

/** Lo que, además de lo de precio y los bloqueantes, le llega a Diego en la bitácora al sacar el PDF: la ley y el GDMTH de BC. */
export function esAvisoParaDiego(a: Pick<Aviso, 'codigo' | 'mensaje'>): boolean {
  return esAvisoDeLaLeyParaDiego(a) || esAvisoGdmthBajaCalifornia(a);
}

/** hoy23 (Diego, 21-oct): cómo se rotula en la bitácora de Diego lo que lleva un PDF, cada cosa por lo que es. La ley dice
 *  ley; el ámbar del GDMTH de San Luis Río Colorado / Puerto Peñasco va «para que los revises» (antes salía rotulado «avisos
 *  del límite de la ley (0.7 MW)»); «avisos rojos como sugerencia» solo si los hay (sin el dato, como antes: se dice). */
export function rotuloAvisosParaDiego(x: { topeLey?: boolean; paraRevisar?: boolean; sinOk?: boolean; rojos?: boolean }): string {
  const t = `${x.topeLey ? 'avisos del límite de la ley (0.7 MW), ' : ''}${x.paraRevisar ? 'avisos para que los revises, ' : ''}${x.sinOk ? 'precio sin tu OK y ' : ''}${x.rojos !== false ? 'avisos rojos como sugerencia' : ''}`;
  return t.replace(/(, | y )$/, '') || 'avisos';
}

/** Los avisos ROJOS del límite de la ley: una opción con 0.7 MW o más de inversores. Con alguno, el PDF NO sale (excepción
 *  explícita de Diego a «nada detiene el PDF»): «No se puede cotizar arriba de 0.7 MW». */
export function avisosArribaDeLaLey<T extends Pick<Aviso, 'codigo' | 'mensaje' | 'nivel'>>(avisos: readonly T[]): T[] {
  return avisos.filter((a) => a.nivel === 'rojo' && esAvisoLimiteLey(a));
}

/** El mensaje de una línea cuando el PDF no sale por el límite de la ley. */
export const NO_SE_PUEDE_COTIZAR_LEY = 'No se puede cotizar arriba de 0.7 MW';

/** Lo que el cliente YA tiene y cuenta para el límite junto con lo nuevo: ampliación con inversor NUEVO (con
 *  «inversor_actual» las opciones ya repiten ese inversor y no se suma dos veces). Sin sus inversores capturados (en
 *  precotización se permite) se cuentan sus kWp como kW de inversores, lo seguro: un sistema así casi nunca lleva más kW de
 *  inversores que kWp de paneles, y la regla del cotizador le daría al menos kWp ÷ 1.30 (CD/CA trifásico, z14). Verificación adversarial del 14-oct:
 *  contaba 0 kW y salió el PDF de una ampliación que, con los 400 kWp que ya tenía, pasaba de 0.9 MW.
 *  `sin_datos` = ampliación sin kWp, paneles ni inversores de lo que ya tiene (no se puede contar). */
export function caQueYaTiene(c: Pick<Cuestionario, 'ampliacion'>): { kw: number; estimado: boolean; sin_datos: boolean; por_paneles: boolean } {
  const ex = c.ampliacion.es_ampliacion ? c.ampliacion.existente : null;
  if (!ex || ex.conexion === 'inversor_actual') return { kw: 0, estimado: false, sin_datos: false, por_paneles: false };
  const r2 = (x: number) => Math.round(x * 100) / 100;
  if (ex.inversores?.length) return { kw: r2(ex.inversores.reduce((s, i) => s + i.kw * i.cantidad, 0)), estimado: false, sin_datos: false, por_paneles: false };
  const ya = kwpQueYaTiene(c);
  return ya.kwp > 0 ? { kw: ya.kwp, estimado: true, sin_datos: false, por_paneles: ya.por_paneles } : { kw: 0, estimado: false, sin_datos: true, por_paneles: false };
}

/** Los watts por panel con que se estima lo que ya tiene cuando solo se capturó CUÁNTOS paneles: el del cotizador (TCL 620 W),
 *  lo seguro (los paneles de antes casi siempre son más chicos). Verificación adversarial del 14-oct: con «Paneles» y sin
 *  «Watts por panel» contaba 0 kWp y se saltaba el tope de 0.7 MW. */
export const PANEL_W_ESTIMADO_AMPLIACION = 620;

/** Los kWp que el cliente YA tiene en una ampliación (para el límite de la ley en kWp, GD-07-AMBAS, y por kW de inversores si no
 *  se capturaron): sus kWp, sus paneles × watts, o sus paneles × 620 W (`por_paneles`, estimado). 0 = no se sabe. */
export function kwpQueYaTiene(c: Pick<Cuestionario, 'ampliacion'>): { kwp: number; por_paneles: boolean } {
  const ex = c.ampliacion.es_ampliacion ? c.ampliacion.existente : null;
  if (!ex) return { kwp: 0, por_paneles: false };
  const r2 = (x: number) => Math.round(x * 100) / 100;
  if (ex.kwp) return { kwp: r2(ex.kwp), por_paneles: false };
  if (ex.paneles && ex.panel_w) return { kwp: r2((ex.paneles * ex.panel_w) / 1000), por_paneles: false };
  if (ex.paneles) return { kwp: r2((ex.paneles * PANEL_W_ESTIMADO_AMPLIACION) / 1000), por_paneles: true };
  return { kwp: 0, por_paneles: false };
}

/** El texto de lo estimado, para los avisos y el candado del PDF. */
export function comoSeContoLoQueTiene(ya: { estimado: boolean; por_paneles: boolean }): string {
  return ya.por_paneles ? `contados por sus paneles × ${PANEL_W_ESTIMADO_AMPLIACION} W` : ya.estimado ? 'contados por sus kWp' : '';
}

/** Ampliación sin el tamaño de lo que ya tiene (ni kWp, ni paneles, ni inversores): el límite de la ley no se puede revisar y
 *  el PDF NO sale (verificación adversarial del 14-oct; ADENDA «LÍMITE LEGAL: SI NO SE PUEDE, NO SE PUEDE»). */
export const FALTA_TAMANO_AMPLIACION = 'Ampliación: captura cuántos paneles o kWp ya tiene: sin eso no se puede revisar el límite de la ley.';
export function faltaTamanoAmpliacion(c: Pick<Cuestionario, 'ampliacion'>): string | null {
  if (!c.ampliacion.es_ampliacion) return null;
  const ex = c.ampliacion.existente;
  return !ex || (!ex.kwp && !ex.paneles && !ex.inversores?.length) ? FALTA_TAMANO_AMPLIACION : null;
}

/** Diego, 24-oct («que obligue al vendedor a ponerle los viáticos en el costo, siempre que sea fuera»): con el rojo
 *  FALTAN_VIATICOS del motor el PDF NO sale (segunda excepción a «nada detiene el PDF», junto al límite de la ley). Lo miden
 *  igual el servidor, el servidor simulado y la pantalla. Devuelve el texto para el vendedor, o null. */
export function faltanViaticos(avisos: readonly Pick<Aviso, 'codigo' | 'nivel' | 'mensaje'>[]): string | null {
  // ADENDA z46-7 (P19): la sucursal de un multisitio cuyos viáticos van en OTRA sucursal de su ciudad (info VIATICOS_DE_LA_CIUDAD)
  // sale en el PDF de todas sin viáticos, pero SOLA no sale sin los suyos (como cualquier obra fuera de Hermosillo). El PDF de
  // todas no usa esta función: reparte los viáticos por ciudad (viaticosPorCiudad).
  return (
    avisos.find((a) => a.nivel === 'rojo' && a.codigo === 'FALTAN_VIATICOS')?.mensaje ??
    avisos.find((a) => a.nivel === 'info' && a.codigo === 'VIATICOS_DE_LA_CIUDAD')?.mensaje ??
    null
  );
}

/** Los rojos del motor que DETIENEN el PDF (las excepciones a «nada detiene el PDF», además del límite de la ley): viáticos
 *  fuera de Hermosillo (faltanViaticos) y un AISLADO sin sus equipos (E7, Juan Carlos 23-oct: sin equipos no hay baterías ni
 *  precio). Lo miden igual el servidor, el servidor simulado y la pantalla. Devuelve el texto para el vendedor, o null. */
export const CODIGOS_QUE_DETIENEN_EL_PDF: readonly CodigoAviso[] = Object.freeze(['PROMO_VENCIDA', 'PROMO_NO_APLICA', 'FALTAN_VIATICOS', 'FALTAN_EQUIPOS_AISLADO', 'AISLADO_SIN_CD', 'AUMENTO_NO_CABE', 'SIN_CADENAS', 'INVERSOR_BLOQUEADO', 'INVERSOR_NO_ALCANZA', 'BATERIA_NO_ALCANZA', 'AMPLIACION_INCOMPLETA', 'AMPLIACION_NOCHE', 'BATERIAS_SIN_RESPALDO', 'SIN_HIBRIDO_APTO', 'RECORTE_NO_SE_OFRECE', 'MESES_IA_EN_CASA']);
/** Lo que le falta al AISLADO para su PDF y DÓNDE se arregla: sus equipos (FALTAN_EQUIPOS_AISLADO: el cuadro de cargas) o,
 *  verificación de la E7 (25-oct), un inversor con baterías que reciba sus paneles en CD (AISLADO_SIN_CD: sin red no funciona
 *  como se cotizó; se arregla en el inversor, /resultado/respaldo, como dice su aviso). Antes se llamaba faltanEquiposAislado y
 *  mandaba al vendedor a los equipos aunque lo que faltaba era el inversor (verificación de E7b). Verificador de R3 (25-oct): ya no
 *  es solo del aislado (también AUMENTO_NO_CABE y SIN_CADENAS): se llamaba rojoQueDetieneElPdf y el PDF de todas las sucursales le
 *  decía «es un aislado» a una casa; ahora rojoQueDetieneElPdf, con el texto neutro. */
/** ADENDA z52-2 (z48-5): el amarillo «falta calcular cuánto: usa «Calcular el aumento»» del motor lleva su botón, que abre el
 *  cuestionario en el aumento con IA. */
export const TEXTO_BOTON_AUMENTO = 'Calcular el aumento';
export function esAvisoAumentoPendiente(a: Pick<Aviso, 'codigo' | 'campo' | 'mensaje'>): boolean {
  return a.codigo === 'FALTA_DATO' && a.campo === '/cuestionario/consumo' && a.mensaje.includes(`«${TEXTO_BOTON_AUMENTO}»`);
}

export const CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF = Object.freeze({ PROMO_VENCIDA: '/parametros/promo', PROMO_NO_APLICA: '/parametros/promo', FALTAN_EQUIPOS_AISLADO: '/cuestionario/respaldo/cargas', AISLADO_SIN_CD: '/resultado/respaldo', AUMENTO_NO_CABE: '/cuestionario/consumo/consumo_futuro_kwh_anual', SIN_CADENAS: '/parametros/opciones_activas', AMPLIACION_INCOMPLETA: '/piezas', AMPLIACION_NOCHE: '/piezas/hoja2', BATERIAS_SIN_RESPALDO: '/cuestionario/respaldo', SIN_HIBRIDO_APTO: '/resultado/respaldo', INVERSOR_BLOQUEADO: '/parametros/equipo_respaldo', INVERSOR_NO_ALCANZA: '/parametros/equipo_respaldo', BATERIA_NO_ALCANZA: '/parametros/modulos_respaldo', RECORTE_NO_SE_OFRECE: '/cuestionario/recorte_punta', MESES_IA_EN_CASA: '/lecturas/A/historial' } as const);
// Verificación del 28-oct noche: INVERSOR_BLOQUEADO e INVERSOR_NO_ALCANZA (y BATERIA_NO_ALCANZA) estaban en CODIGOS_QUE_DETIENEN_EL_PDF
// —la pantalla y su aviso decían «el PDF no sale»— pero no aquí, y el servidor sacaba el PDF igual. Las dos listas son la misma (salvo
// los viáticos, que se miden aparte con faltanViaticos): si alguien agrega un código a una sola, truena al cargar.
for (const c of CODIGOS_QUE_DETIENEN_EL_PDF)
  if (c !== 'FALTAN_VIATICOS' && !Object.hasOwn(CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF, c)) throw new Error(`avisos-vista: ${c} está en CODIGOS_QUE_DETIENEN_EL_PDF y no en CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF: el servidor sacaría el PDF`);
for (const c of Object.keys(CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF) as CodigoAviso[])
  if (!CODIGOS_QUE_DETIENEN_EL_PDF.includes(c)) throw new Error(`avisos-vista: ${c} detiene el PDF en el servidor y la pantalla no lo dice (falta en CODIGOS_QUE_DETIENEN_EL_PDF)`);
/** hoy76 (verificación, 29-oct): el rojo FALTAN_EQUIPOS_AISLADO del aislado sin equipos cuyas baterías prometen horas que la
 *  propuesta no acepta sale también por MÓDULOS FIJADOS a mano en Opciones; entonces su texto pide bajarlos y lleva este campo.
 *  «Ir a arreglarlo» y el 400 del PDF lo respetan (antes mandaban siempre a los equipos, aunque el texto dijera «Opciones»). */
export const CAMPO_MODULOS_FIJADOS = '/parametros/modulos_respaldo';
/** Dónde se arregla un rojo que detiene el PDF: el campo fijo de su código; solo el del aislado con módulos fijados va a Opciones.
 *  null = no detiene el PDF. */
export function campoDelRojoQueDetiene(a: Pick<Aviso, 'codigo'> & { campo?: string | null | undefined }): string | null {
  if (!Object.hasOwn(CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF, a.codigo)) return null;
  if (a.codigo === 'FALTAN_EQUIPOS_AISLADO' && a.campo === CAMPO_MODULOS_FIJADOS) return CAMPO_MODULOS_FIJADOS;
  return CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF[a.codigo as keyof typeof CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF];
}
export function rojoQueDetieneElPdf(avisos: readonly (Pick<Aviso, 'codigo' | 'nivel' | 'mensaje'> & { campo?: string | null | undefined })[]): { mensaje: string; campo: string } | null {
  // Barrido del 25-oct (R3): también la Holgada que no cubre el consumo futuro (AUMENTO_NO_CABE) y la opción de 1 panel
  // (SIN_CADENAS): sin PDF y sin bitácora PDF_FALLO, con el campo donde se arregla.
  const a = avisos.find((x) => x.nivel === 'rojo' && Object.hasOwn(CAMPO_DEL_ROJO_QUE_DETIENE_EL_PDF, x.codigo));
  return a ? { mensaje: a.mensaje, campo: campoDelRojoQueDetiene(a)! } : null;
}
export function pdfDetenidoPor(avisos: readonly Pick<Aviso, 'codigo' | 'nivel' | 'mensaje'>[]): string | null {
  return faltanViaticos(avisos) ?? rojoQueDetieneElPdf(avisos)?.mensaje ?? null;
}

/** Las opciones que pasan el límite de la ley contando lo que ya tiene (≥ 700 kW de inversores): el PDF no sale. Lo miden
 *  igual el servidor, el servidor simulado y la pantalla, sobre las opciones mismas (no solo por el aviso del motor). */
export function opcionesArribaDeLaLey(opciones: readonly { id: OpcionId; inversores: readonly { kw: number; cantidad: number }[] }[], c: Pick<Cuestionario, 'ampliacion'>): string[] {
  const ya = caQueYaTiene(c);
  const kw1 = (x: number) => numero(x, x % 1 ? 1 : 0);
  return opciones.flatMap((o) => {
    const kw = Math.round((o.inversores.reduce((s, i) => s + i.kw * i.cantidad, 0) + ya.kw) * 100) / 100;
    return kw >= LIMITE_LEY_GD_KW ? [`${NOMBRE_OPCION[o.id]}: ${kw1(kw)} kW de inversores${ya.kw > 0 ? `, con lo que ya tiene (${kw1(ya.kw)} kW${ya.estimado ? `, ${comoSeContoLoQueTiene(ya)}` : ''})` : ''}`] : [];
  });
}

/** Cuántos avisos se ven sin abrir nada. */
export const MAXIMO_AVISOS_A_LA_VISTA = 3;

/** Línea corta por código, para cuando el mismo aviso sale en varias opciones con cifras distintas. */
const TITULO_CORTO: Readonly<Partial<Record<CodigoAviso, string>>> = Object.freeze({
  PIERDE_DINERO: 'con este precio Solarix pierde dinero: sugerencia: Diego recibe el aviso.',
  DEBAJO_DEL_PISO: 'el precio por watt quedó debajo del piso: sugerencia: Diego recibe el aviso.',
  MARGEN_BAJO: 'el margen de este precio quedó bajo.',
  DESCUENTO_SIN_APROBAR: 'el precio quedó abajo de la tabla más de lo permitido: necesita OK.',
  REGULACION: 'hay una regla de interconexión que revisar.',
  FALTAN_RECIBOS: 'faltan recibos del año que se cotiza: tócalo para ver cuántos y de qué medidor.',
});

const NOMBRE_OPCION: Readonly<Record<OpcionId, string>> = Object.freeze({ arranque: 'Arranque', exacta: 'Exacta', holgada: 'Holgada' });

/** Primera oración de un texto (el punto que cierra va seguido de espacio y mayúscula: «7.5 %» no corta). */
export function primeraOracion(texto: string): string {
  const t = texto.trim();
  const m = /^(.+?[.!?])\s+(?=[A-ZÁÉÍÓÚÑ¿¡«(])/su.exec(t);
  return m ? m[1]! : t;
}

/** z16/z23 (Diego, 14 y 20-oct): en una casa 1F el motor nombra «Mejor retorno» a la opción arranque cuando lleva los paneles
 *  con el menor retorno (así la nombran también la pantalla y el PDF): el aviso lo dice en su texto y aquí se respeta. */
function nombreDe(a: Aviso): string {
  if (!a.opcion) return '';
  return a.opcion === 'arranque' && /^Mejor retorno[ :(]/u.test(a.mensaje.trim()) ? 'Mejor retorno' : NOMBRE_OPCION[a.opcion];
}

/** Quita el «Exacta: » del principio cuando el aviso es de una opción (para juntar el mismo aviso de varias opciones). */
function sinOpcion(a: Aviso): string {
  if (!a.opcion) return a.mensaje.trim();
  const nombre = nombreDe(a);
  const m = a.mensaje.trim();
  return m.startsWith(`${nombre}:`) ? m.slice(nombre.length + 1).trim() : m;
}

function minusculaInicial(t: string): string {
  return t ? t[0]!.toLowerCase() + t.slice(1) : t;
}
function mayusculaInicial(t: string): string {
  return t ? t[0]!.toUpperCase() + t.slice(1) : t;
}

function unir(nombres: readonly string[]): string {
  if (nombres.length <= 1) return nombres[0] ?? '';
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/** Un aviso «a la vista»: uno o varios avisos iguales juntados en una línea corta. */
export interface AvisoALaVista {
  /** Llave estable (código, nivel y texto sin la opción ni las cifras). */
  llave: string;
  codigo: CodigoAviso;
  nivel: Aviso['nivel'];
  /** La línea corta: «Arranque y Holgada: …» (primera oración). */
  corto: string;
  /** Los avisos que junta (para enseñar el texto completo, la fuente y las acciones al tocarlo). */
  avisos: Aviso[];
}

/** ¿Este aviso merece estar a la vista? Abierto, no informativo, que no venga de la revisión con IA (Diego: de la IA solo
 *  interesa el resumen de rentabilidad) y de un código importante. Un ROJO de otro código (p. ej. el PDF que va a tronar)
 *  también compite por la vista, detrás de los de la lista (un rojo no se queda fuera por no estar en la lista). */
export function esAvisoImportante(a: Aviso): boolean {
  if (a.resuelto || a.nivel === 'info' || a.origen === 'ia_revision') return false;
  // El margen en la zona de alerta (amarillo) es menor: a la vista solo el que necesita el OK (rojo).
  if (a.codigo === 'MARGEN_BAJO' && a.nivel !== 'rojo') return false;
  return a.nivel === 'rojo' || CODIGOS_AVISO_IMPORTANTES.includes(a.codigo) || esAvisoGdmthBajaCalifornia(a);
}

const RANGO: Readonly<Record<Aviso['nivel'], number>> = Object.freeze({ rojo: 0, amarillo: 1, info: 2 });

/** Códigos que significan una sola cosa por cotización aunque los emitan dos fuentes con distinto texto (recorrido del
 *  13-oct a 390 px: el aviso de facturación salía DOS veces a la vista y un rojo se iba a «Ver todos»). */
//  Tanda 1c: «faltan recibos» lo dicen la lectura («Tarifa GDMTH: llevas 11 de 12») y el sistema («Medidor A: llevas 11 de 12»).
const CODIGOS_UN_SOLO_GRUPO: readonly CodigoAviso[] = Object.freeze(['TARIFA_NO_COINCIDE', 'FACTURACION_NO_COINCIDE', 'TITULAR_NO_COINCIDE', 'FALTAN_RECIBOS'] as const);

/** Junta los avisos repetidos (mismo código y mismo texto, aunque sea de otra opción o con otras cifras) y los ordena por
 *  importancia: el orden de CODIGOS_AVISO_IMPORTANTES y, dentro del mismo código, rojo antes que amarillo. */
export function juntarAvisos(avisos: readonly Aviso[]): AvisoALaVista[] {
  const grupos = new Map<string, AvisoALaVista>();
  for (const a of avisos) {
    const texto = primeraOracion(sinOpcion(a));
    // El lector y el motor dicen lo mismo con otras palabras («bimestral y el recibo es mensual» / «el recibo es mensual y
    // el cuestionario dice bimestral»): estos códigos son UNA sola cosa por cotización y van en un solo grupo.
    const llave = CODIGOS_UN_SOLO_GRUPO.includes(a.codigo) ? `${a.codigo}|${a.nivel}` : `${a.codigo}|${a.nivel}|${texto.replace(/[\d.,$%]+/g, '#').toLowerCase()}`;
    const g = grupos.get(llave);
    if (g) g.avisos.push(a);
    else grupos.set(llave, { llave, codigo: a.codigo, nivel: a.nivel, corto: '', avisos: [a] });
  }
  for (const g of grupos.values()) {
    // el nombre de cada opción como lo dicen sus avisos («Mejor retorno» si alguno de la arranque lo dice; si no, el de siempre)
    const nombres = new Map<OpcionId, string>();
    for (const a of g.avisos) if (a.opcion && (!nombres.has(a.opcion) || nombreDe(a) !== NOMBRE_OPCION[a.opcion])) nombres.set(a.opcion, nombreDe(a));
    const opciones = [...nombres.keys()];
    const textos = new Set(g.avisos.map((a) => primeraOracion(sinOpcion(a))));
    const texto = textos.size > 1 && TITULO_CORTO[g.codigo] ? TITULO_CORTO[g.codigo]! : primeraOracion(sinOpcion(g.avisos[0]!));
    // «Holgada (788.6 kWp de paneles): …» ya nombra su opción: no se le antepone otra vez (salía «Holgada: holgada (…»).
    const yaLaNombra = opciones.length === 1 && texto.startsWith(`${nombres.get(opciones[0]!)!} (`);
    g.corto = yaLaNombra ? texto : opciones.length ? `${unir(opciones.map((o) => nombres.get(o)!))}: ${minusculaInicial(texto)}` : mayusculaInicial(texto);
  }
  const orden = (g: AvisoALaVista) => {
    // Diego, 13-oct (22:30): el límite de la ley (0.7 MW) va PRIMERO, antes que el precio y que cualquier rojo; junto a él,
    // el amarillo de la ley que pide el OK de Diego (los paneles con 700 kWp o más).
    if (g.avisos.some(esAvisoDeLaLeyParaDiego)) return -10 + RANGO[g.nivel];
    // z20: el GDMTH de Baja California (pide revisión de Diego) justo detrás de la ley
    if (g.avisos.some(esAvisoGdmthBajaCalifornia)) return -5 + RANGO[g.nivel];
    const i = CODIGOS_AVISO_IMPORTANTES.indexOf(g.codigo);
    return (i < 0 ? CODIGOS_AVISO_IMPORTANTES.length : i) * 10 + RANGO[g.nivel];
  };
  return [...grupos.values()].sort((x, y) => orden(x) - orden(y));
}

/** Lo que la pantalla enseña: hasta 3 importantes juntados (a la vista) y TODOS los avisos para «Ver todos (N)». */
/** Los de PRECIO dicen en el fondo lo mismo («bájale menos»): a la vista va solo el primero (verificación del 13-oct: tres
 *  de precio llenaban las 3 plazas y un rojo de la ley se iba a «Ver todos» sin marca). */
const CODIGOS_DE_PRECIO: readonly CodigoAviso[] = Object.freeze(['PIERDE_DINERO', 'DEBAJO_DEL_PISO', 'MARGEN_BAJO', 'DESCUENTO_SIN_APROBAR'] as const);

/** Hasta `maximo` grupos importantes, uno solo de precio; `rojos_ocultos` = rojos que no cupieron (la pantalla lo dice). */
export function elegirALaVista(grupos: readonly AvisoALaVista[], maximo = MAXIMO_AVISOS_A_LA_VISTA): { importantes: AvisoALaVista[]; rojos_ocultos: number } {
  const importantes: AvisoALaVista[] = [];
  let conPrecio = false;
  let rojosOcultos = 0;
  for (const g of grupos) {
    const dePrecio = CODIGOS_DE_PRECIO.includes(g.codigo);
    if (importantes.length < maximo && !(dePrecio && conPrecio)) {
      importantes.push(g);
      if (dePrecio) conPrecio = true;
    } else if (g.nivel === 'rojo' && !(dePrecio && conPrecio)) rojosOcultos++;
  }
  return { importantes, rojos_ocultos: rojosOcultos };
}

export function avisosALaVista(avisos: readonly Aviso[], maximo = MAXIMO_AVISOS_A_LA_VISTA): { importantes: AvisoALaVista[]; rojos_ocultos: number; todos: readonly Aviso[] } {
  return { ...elegirALaVista(juntarAvisos(avisos.filter(esAvisoImportante)), maximo), todos: avisos };
}

/** Diego, 13-oct (segunda tanda): de la revisión con IA solo interesa el RESUMEN de rentabilidad. Sus hallazgos no se
 *  pierden pero bajan a «info»: no piden nota, no cuentan como rojos y no salen a la vista (quedan en «Ver todos»). */
export function comoInfo(avisos: readonly Aviso[]): Aviso[] {
  return avisos.map((a) => (a.nivel === 'info' ? a : { ...a, nivel: 'info' as const }));
}
